// context/SearchContext.jsx - Search context for global search functionality
import React, { createContext, useContext, useCallback, useMemo, useRef } from 'react';
import { useBaseContext, createBaseState } from './shared/useBaseContext';
import { createErrorHandler, handleAsyncOperation } from './shared/errorHandler';
import { searchAll, searchPlayers, searchTeams, searchTournaments, getSearchSuggestions, loadTeamOwner, loadTeamMembers, loadTeamStats } from '../services/searchService';

const SearchContext = createContext();

// Search-specific actions
const searchActions = {
  SET_SEARCH_RESULTS: 'SET_SEARCH_RESULTS',
  SET_SEARCH_QUERY: 'SET_SEARCH_QUERY',
  SET_SEARCH_SUGGESTIONS: 'SET_SEARCH_SUGGESTIONS',
  SET_SEARCH_HISTORY: 'SET_SEARCH_HISTORY',
  CLEAR_SEARCH: 'CLEAR_SEARCH',
  SET_SEARCH_FILTERS: 'SET_SEARCH_FILTERS'
};

// Search reducer
const searchReducer = (state, action) => {
  switch (action.type) {
    case searchActions.SET_SEARCH_RESULTS:
      return {
        ...state,
        results: action.payload.results,
        query: action.payload.query,
        loading: false,
        hasSearched: true
      };

    case searchActions.SET_SEARCH_QUERY:
      return {
        ...state,
        query: action.payload,
        loading: action.payload.trim().length >= 2
      };

    case searchActions.SET_SEARCH_SUGGESTIONS:
      return {
        ...state,
        suggestions: action.payload
      };

    case searchActions.SET_SEARCH_HISTORY:
      return {
        ...state,
        searchHistory: action.payload
      };

    case searchActions.CLEAR_SEARCH:
      return {
        ...state,
        results: {
          players: [],
          teams: [],
          tournaments: [],
          combined: [],
          total: 0
        },
        query: '',
        suggestions: [],
        loading: false,
        hasSearched: false
      };

    case searchActions.SET_SEARCH_FILTERS:
      return {
        ...state,
        filters: { ...state.filters, ...action.payload }
      };

    default:
      return state;
  }
};

const initialState = createBaseState({
  results: {
    players: [],
    teams: [],
    tournaments: [],
    combined: [],
    total: 0
  },
  query: '',
  suggestions: [],
  searchHistory: [],
  loading: false,
  hasSearched: false,
  filters: {
    includeUsers: true,
    includeTeams: true,
    includeTournaments: true,
    limit: 10
  }
});

export const SearchProvider = ({ children }) => {
  const { state, dispatch, clearError } = useBaseContext(initialState, searchReducer);
  const errorHandler = useMemo(() => createErrorHandler('SEARCH'), []);
  
  // Refs for managing search debouncing and caching
  const searchTimeoutRef = useRef(null);
  const suggestionTimeoutRef = useRef(null);
  const searchCacheRef = useRef(new Map());
  const lastQueryRef = useRef('');

  // Helper function to save search to memory-based history
  const addToSearchHistory = useCallback((query, results) => {
    if (!query.trim() || results.total === 0) return;

    try {
      const currentHistory = state.searchHistory || [];
      const newEntry = {
        query: query.trim(),
        timestamp: Date.now(),
        resultCount: results.total
      };

      // Remove duplicate queries and keep only recent 10
      const filteredHistory = currentHistory
        .filter(entry => entry.query.toLowerCase() !== query.toLowerCase())
        .slice(0, 9);

      const updatedHistory = [newEntry, ...filteredHistory];
      
      dispatch({ type: searchActions.SET_SEARCH_HISTORY, payload: updatedHistory });
    } catch (error) {
      console.warn('Failed to save search history:', error);
    }
  }, [dispatch, state.searchHistory]);

  // Load search history from memory (no-op since we're using memory storage)
  const loadSearchHistory = useCallback(() => {
    // History is already managed in memory, no need to load from external storage
    // Search history loaded from memory
  }, []);

  // Clear search history
  const clearSearchHistory = useCallback(() => {
    try {
      dispatch({ type: searchActions.SET_SEARCH_HISTORY, payload: [] });
    } catch (error) {
      console.warn('Failed to clear search history:', error);
    }
  }, [dispatch]);

  // Perform comprehensive search
  const performSearch = useCallback(async (query, options = {}) => {
    return handleAsyncOperation(async () => {
      const trimmedQuery = query.trim();
      
      if (trimmedQuery.length < 2) {
        dispatch({ type: searchActions.CLEAR_SEARCH });
        return { success: true, data: { players: [], teams: [], tournaments: [], total: 0 } };
      }

      // Check cache first
      const cacheKey = `${trimmedQuery}:${JSON.stringify(options)}`;
      if (searchCacheRef.current.has(cacheKey)) {
        const cachedResult = searchCacheRef.current.get(cacheKey);
        dispatch({
          type: searchActions.SET_SEARCH_RESULTS,
          payload: { results: cachedResult, query: trimmedQuery }
        });
        return { success: true, data: cachedResult };
      }

      clearError();
      
      const searchOptions = {
        ...state.filters,
        ...options
      };

      const result = await searchAll(trimmedQuery, searchOptions);
      
      if (result.success) {
        // Cache the result for 5 minutes
        searchCacheRef.current.set(cacheKey, result.data);
        setTimeout(() => {
          searchCacheRef.current.delete(cacheKey);
        }, 5 * 60 * 1000);

        dispatch({
          type: searchActions.SET_SEARCH_RESULTS,
          payload: { results: result.data, query: trimmedQuery }
        });

        // Add to search history
        addToSearchHistory(trimmedQuery, result.data);

        return result;
      } else {
        throw new Error(result.error);
      }
    }, errorHandler);
  }, [state.filters, dispatch, clearError, addToSearchHistory, errorHandler]);

  // Debounced search function
  const debouncedSearch = useCallback((query, options = {}, delay = 500) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    dispatch({ type: searchActions.SET_SEARCH_QUERY, payload: query });

    if (query.trim().length < 2) {
      dispatch({ type: searchActions.CLEAR_SEARCH });
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      performSearch(query, options);
    }, delay);
  }, [dispatch, performSearch]);

  // Get search suggestions with debouncing
  const getSuggestions = useCallback(async (query) => {
    return handleAsyncOperation(async () => {
      const trimmedQuery = query.trim();
      
      if (trimmedQuery.length < 1) {
        dispatch({ type: searchActions.SET_SEARCH_SUGGESTIONS, payload: [] });
        return { success: true, data: [] };
      }

      const result = await getSearchSuggestions(trimmedQuery);
      
      if (result.success) {
        dispatch({ type: searchActions.SET_SEARCH_SUGGESTIONS, payload: result.data });
        return result;
      } else {
        throw new Error(result.error);
      }
    }, errorHandler);
  }, [dispatch, errorHandler]);

  // Debounced suggestions
  const debouncedSuggestions = useCallback((query, delay = 300) => {
    if (suggestionTimeoutRef.current) {
      clearTimeout(suggestionTimeoutRef.current);
    }

    suggestionTimeoutRef.current = setTimeout(() => {
      getSuggestions(query);
    }, delay);
  }, [getSuggestions]);

  // Search specific categories
  const searchPlayersByQuery = useCallback(async (query, limit = 10) => {
    return handleAsyncOperation(async () => {
      const result = await searchPlayers(query, limit);
      return result;
    }, errorHandler);
  }, [errorHandler]);

  const searchTeamsByQuery = useCallback(async (query, limit = 10) => {
    return handleAsyncOperation(async () => {
      const result = await searchTeams(query, limit);
      return result;
    }, errorHandler);
  }, [errorHandler]);

  const searchTournamentsByQuery = useCallback(async (query, limit = 10) => {
    return handleAsyncOperation(async () => {
      const result = await searchTournaments(query, limit);
      return result;
    }, errorHandler);
  }, [errorHandler]);

  // Update search filters
  const updateFilters = useCallback((newFilters) => {
    dispatch({ type: searchActions.SET_SEARCH_FILTERS, payload: newFilters });
    
    // Re-search with new filters if we have a current query
    if (state.query.trim().length >= 2) {
      performSearch(state.query, newFilters);
    }
  }, [dispatch, state.query, performSearch]);

  // Clear search results and query
  const clearSearch = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (suggestionTimeoutRef.current) {
      clearTimeout(suggestionTimeoutRef.current);
    }
    
    dispatch({ type: searchActions.CLEAR_SEARCH });
    lastQueryRef.current = '';
  }, [dispatch]);

  // Quick search function for immediate results
  const quickSearch = useCallback(async (query) => {
    return performSearch(query, { limit: 5 });
  }, [performSearch]);

  // Lazy loading functions for team data
  const lazyLoadTeamOwner = useCallback(async (ownerId) => {
    return handleAsyncOperation(async () => {
      return await loadTeamOwner(ownerId);
    }, errorHandler);
  }, [errorHandler]);

  const lazyLoadTeamMembers = useCallback(async (teamId) => {
    return handleAsyncOperation(async () => {
      return await loadTeamMembers(teamId);
    }, errorHandler);
  }, [errorHandler]);

  const lazyLoadTeamStats = useCallback(async (teamId) => {
    return handleAsyncOperation(async () => {
      return await loadTeamStats(teamId);
    }, errorHandler);
  }, [errorHandler]);

  // Get popular searches from history
  const getPopularSearches = useCallback(() => {
    return state.searchHistory
      .filter(entry => entry.resultCount > 0)
      .sort((a, b) => b.resultCount - a.resultCount)
      .slice(0, 5)
      .map(entry => entry.query);
  }, [state.searchHistory]);

  // Get recent searches
  const getRecentSearches = useCallback(() => {
    return state.searchHistory
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 5)
      .map(entry => entry.query);
  }, [state.searchHistory]);

  // Initialize search history on mount
  React.useEffect(() => {
    loadSearchHistory();
  }, [loadSearchHistory]);

  // Cleanup timeouts on unmount
  React.useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (suggestionTimeoutRef.current) {
        clearTimeout(suggestionTimeoutRef.current);
      }
    };
  }, []);

  const value = {
    // State
    ...state,
    
    // Search functions
    search: performSearch,
    debouncedSearch,
    quickSearch,
    
    // Suggestions
    getSuggestions,
    debouncedSuggestions,
    
    // Category-specific search
    searchPlayers: searchPlayersByQuery,
    searchTeams: searchTeamsByQuery,
    searchTournaments: searchTournamentsByQuery,
    
    // Lazy loading functions
    loadTeamOwner: lazyLoadTeamOwner,
    loadTeamMembers: lazyLoadTeamMembers,
    loadTeamStats: lazyLoadTeamStats,
    
    // Filters
    updateFilters,
    
    // History
    loadSearchHistory,
    clearSearchHistory,
    getPopularSearches,
    getRecentSearches,
    
    // Utilities
    clearSearch,
    clearError,
    
    // Helper getters
    hasResults: state.results.total > 0,
    isEmpty: state.results.total === 0 && state.hasSearched,
    isSearching: state.loading
  };

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

export default SearchContext;
